import { useCallback, useMemo } from 'react';
import type { useFilterCBPayAssetsByDestinationWalletsFragment$key } from '@onramp/data/__generated__/useFilterCBPayAssetsByDestinationWalletsFragment.graphql';
import { useDestinationWalletsV2 } from '@onramp/state/recoil/appParamsState';
import { parseStoneIdToNetworkId } from '@onramp/utils/blockchains/stoneIdFormat';
import { validate as validateUuid } from 'uuid';
import { graphql, readInlineData } from '@cbhq/data-layer';

const useFilterCBPayAssetsByDestinationWalletsFragment = graphql`
  fragment useFilterCBPayAssetsByDestinationWalletsFragment on CBPayAsset @inline {
    uuid
    symbol
    networks {
      name
    }
  }
`;

export const useFilterCBPayAssetsByDestinationWallets = () => {
  const { singleAssetsRequested, networksWithAllAssetsRequested } = useDestinationWalletsV2();

  const { assetUuids, assetSymbols } = useMemo(() => {
    const uuids: string[] = [];
    const symbols: string[] = [];

    singleAssetsRequested.forEach((assetIdOrSymbol) => {
      if (validateUuid(assetIdOrSymbol)) {
        uuids.push(assetIdOrSymbol);
      } else {
        symbols.push(assetIdOrSymbol);
      }
    });

    return { assetUuids: uuids, assetSymbols: symbols };
  }, [singleAssetsRequested]);

  return useCallback<(assetRef: useFilterCBPayAssetsByDestinationWalletsFragment$key) => boolean>(
    (assetRef) => {
      const asset = readInlineData(useFilterCBPayAssetsByDestinationWalletsFragment, assetRef);
      /**
       * For the asset to be displayed it needs to have either:
       * 1. Top level network config displayed i.e. networks: "ethereum"
       * 2. Config with the asset directly specified by symbol i.e. assets: "ETH"
       * 3. Config with the asset directly specified by UUID i.e. assets: "d85dce9b-5b73-5c3c-8978-522ce1d1c1b4"
       */

      // Check if asset is requested by symbol
      const isRequestedByAssetSymbol = asset.symbol && assetSymbols.includes(asset.symbol);
      if (isRequestedByAssetSymbol) {
        return true;
      }

      const isRequestedByAssetUuid = asset.uuid && assetUuids.includes(asset.uuid);
      if (isRequestedByAssetUuid) {
        return true;
      }

      const isRequestedByNetwork = asset.networks
        ?.map((network) => parseStoneIdToNetworkId(network?.name))
        .some((stoneId) => stoneId && networksWithAllAssetsRequested.includes(stoneId));
      if (isRequestedByNetwork) {
        return isRequestedByNetwork;
      }

      return false;
    },
    [assetUuids, assetSymbols, networksWithAllAssetsRequested],
  );
};
